/**
 * Fault reporting: what the browser was doing when it stopped doing it.
 *
 * ===========================================================================
 * THE REPORT HAS TO SURVIVE THE TAB BEING KILLED.
 * ===========================================================================
 *
 * This module exists because of a bug that produced no evidence at all. A
 * group match froze while a country was being selected, and the window was
 * closed to get out of it. Nothing threw, so `onerror` would not have fired.
 * Nothing was sent, because a blocked main thread cannot send. And nothing
 * survived, because the tab died holding whatever it knew.
 *
 * So the order is inverted: breadcrumbs are written to `localStorage`
 * SYNCHRONOUSLY as they happen, and the network is only ever a flush of what
 * is already on disk. A fault that kills the tab is reported on the next load
 * instead of being lost. `pagehide` gets a `sendBeacon` too, but that is the
 * optimisation, not the mechanism — the storage write is what makes this work
 * when the browser is dragged out from under it.
 *
 * ===========================================================================
 * THREE THINGS GO WRONG, AND ONLY TWO OF THEM THROW.
 * ===========================================================================
 *
 *   error       an exception reached the top. `onerror`.
 *   rejection   a promise rejected with nobody listening.
 *   stall       the main thread stopped moving. NOTHING REPORTS THIS but a
 *               watchdog, and it is the one that matters here: a frozen tab
 *               looks, from the server, exactly like somebody closing a lid.
 *
 * ===========================================================================
 * WHAT IT DOES NOT COLLECT.
 * ===========================================================================
 *
 * No IP, no referrer, no URL, no cookies, nothing typed. Breadcrumbs are game
 * state — round, rung, phase, and what the globe was asked to do — because
 * that is what localises a fault. `agent` is the one field that is not about
 * the game, and it earns its place by telling an iOS Safari globe bug apart
 * from a desktop Chrome one.
 */

const KEY = 'geogame.fault';
/**
 * Set while a session is running, removed on a clean `pagehide`.
 *
 * Its ABSENCE at startup is the signal, which is why it is a separate key from
 * the fault record: a tab that is killed cannot write anything, so the only
 * thing that can be observed is what it failed to erase.
 */
const OPEN_KEY = 'geogame.fault.open';
/** Breadcrumbs kept. Small: this is written on every phase change. */
const MAX_TRAIL = 30;
const MAX_ENTRY = 200;

/**
 * How late a beat has to be before it counts as a stall.
 *
 * Well above a slow frame or a garbage collection, and well below the point a
 * person gives up and closes the tab. Under 1s this would fire on any laptop
 * waking from sleep, and a reporter that cries wolf gets ignored.
 */
const BEAT_MS = 250;
const STALL_MS = 2_000;

interface Context {
  playerId?: string | null;
  matchId?: string | null;
  roundIdx?: number | null;
  rung?: number | null;
  phase?: string | null;
  gameMode?: string | null;
  tier?: string | null;
}

interface Pending {
  readonly kind: 'error' | 'rejection' | 'stall';
  readonly message: string;
  readonly stallMs: number | null;
  readonly context: Context;
  readonly trail: readonly string[];
  readonly agent: string;
  readonly viewport: string;
}

let trail: string[] = [];
let context: Context = {};
let armed = false;

function apiBase(): string {
  const configured = import.meta.env['VITE_API_URL'];
  return typeof configured === 'string' && configured.length > 0 ? configured : '';
}

/** Never throws. Storage can be disabled, full, or a private-mode stub. */
function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* nothing to do about it, and nothing worth breaking over */
  }
}

/**
 * Note something that just happened.
 *
 * Called on every phase change and every globe interaction, so it has to be
 * cheap and it has to be safe. It writes the whole ring each time rather than
 * appending, because there is no append primitive here and a ring of thirty
 * short strings is a rounding error next to a round of the game.
 */
export function breadcrumb(entry: string): void {
  trail.push(entry.slice(0, MAX_ENTRY));
  if (trail.length > MAX_TRAIL) trail = trail.slice(-MAX_TRAIL);
  if (armed) write(`${KEY}.trail`, JSON.stringify(trail));
}

/** Keep the reporter's idea of where the player is up to date. */
export function setFaultContext(next: Context): void {
  context = { ...context, ...next };
  if (armed) write(`${KEY}.ctx`, JSON.stringify(context));
}

function snapshot(
  kind: Pending['kind'],
  message: string,
  stallMs: number | null,
): Pending {
  return {
    kind,
    message: message.slice(0, 500),
    stallMs,
    context,
    trail: [...trail],
    agent: navigator.userAgent.slice(0, 300),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  };
}

/**
 * Record a fault to disk FIRST, then try the network.
 *
 * The order is the whole design. If the send works, the record is cleared and
 * nothing is sent twice; if the tab dies between the two, the next load finds
 * it and sends it then.
 */
function record(p: Pending): void {
  write(KEY, JSON.stringify(p));
  void send(p).then((ok) => {
    if (ok) clear();
  });
}

function clear(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* see `write` */
  }
}

async function send(p: Pending): Promise<boolean> {
  const payload = {
    kind: p.kind,
    message: p.message,
    stallMs: p.stallMs,
    trail: p.trail,
    agent: p.agent,
    viewport: p.viewport,
    ...p.context,
  };
  try {
    const res = await fetch(`${apiBase()}/api/client-error`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function drop(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* see `write` */
  }
}

/**
 * Send whatever the last session left behind.
 *
 * TWO CASES, AND THE SECOND IS THE WHOLE POINT.
 *
 * A COMPLETE RECORD means the watchdog caught a stall, or something threw, and
 * the snapshot was written but never made it to the network. Send it as-is.
 *
 * AN OPEN MARKER with no record means the last session never reached
 * `pagehide`: the tab was killed while it was still running. That is exactly
 * what happens to a page somebody force-closes to escape a freeze, and it is
 * the case that previously produced no evidence whatsoever. There is no
 * snapshot to send, so one is reconstructed from the breadcrumbs and context
 * that were being written to disk as they happened.
 *
 * It can misfire — a browser crash, an OS kill and a discarded background tab
 * all look the same from here. That is why the kind is `stall` and the message
 * says what was actually observed rather than claiming a freeze: the trail is
 * there to tell them apart.
 */
function flushPending(): void {
  const raw = read(KEY);
  if (raw !== null) {
    clear();
    drop(OPEN_KEY);
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      return;
    }
    if (typeof parsed === 'object' && parsed !== null) void send(parsed as Pending);
    return;
  }

  if (read(OPEN_KEY) === null) return;
  drop(OPEN_KEY);

  const savedTrail = read(`${KEY}.trail`);
  const savedCtx = read(`${KEY}.ctx`);
  let priorTrail: string[] = [];
  let priorCtx: Context = {};
  try {
    const t: unknown = savedTrail === null ? [] : JSON.parse(savedTrail);
    if (Array.isArray(t)) priorTrail = t.filter((e): e is string => typeof e === 'string');
  } catch {
    /* a half-written ring is still worth the context */
  }
  try {
    const c: unknown = savedCtx === null ? {} : JSON.parse(savedCtx);
    if (typeof c === 'object' && c !== null) priorCtx = c as Context;
  } catch {
    /* as above */
  }

  // Nothing was going on. A player who closed the home screen is not a fault.
  if (priorTrail.length === 0) return;

  void send({
    kind: 'stall',
    message: 'tab ended without a clean exit',
    stallMs: null,
    context: priorCtx,
    trail: priorTrail,
    agent: navigator.userAgent.slice(0, 300),
    viewport: `${window.innerWidth}x${window.innerHeight}`,
  });
}

/**
 * Start reporting. Idempotent, and safe to call before React mounts.
 */
export function startFaultReporter(): void {
  if (armed) return;
  armed = true;

  // Flush FIRST: the marker below is about this session, and reading the old
  // one has to happen before it is overwritten.
  flushPending();
  write(OPEN_KEY, '1');
  write(`${KEY}.trail`, '[]');

  window.addEventListener('error', (e) => {
    record(snapshot('error', e.message || 'error', null));
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason: unknown = e.reason;
    const message =
      reason instanceof Error ? reason.message : String(reason ?? 'unhandled rejection');
    record(snapshot('rejection', message, null));
  });

  /*
   * THE WATCHDOG.
   *
   * A blocked main thread cannot notice that it is blocked — but it can notice
   * afterwards, because the timer it set never got to run on time. Every beat
   * measures the gap since the last one; a gap far past the interval is time
   * the thread spent unable to do anything, including render, including
   * respond to a tap.
   *
   * The report is therefore always written AFTER the stall ends. If the stall
   * never ends because the tab is closed, the breadcrumbs on disk are what is
   * left, and `flushPending` sends them next time.
   */
  let last = Date.now();
  window.setInterval(() => {
    const now = Date.now();
    const gap = now - last;
    last = now;
    if (gap >= STALL_MS) {
      record(snapshot('stall', `main thread blocked for ${Math.round(gap)}ms`, Math.round(gap)));
    }
  }, BEAT_MS);

  /*
   * A tab closing cleanly is the easy case, and `sendBeacon` is the only thing
   * that survives it — `fetch` is cancelled on unload however nicely it asks.
   * Only fires when there is something unsent, which is the stall-in-progress
   * case: the breadcrumbs are on disk but the flush has not happened yet.
   */
  window.addEventListener('pagehide', () => {
    // A clean exit. Whatever happens next, it was not a tab that died mid-round.
    drop(OPEN_KEY);
    const raw = read(KEY);
    if (raw === null) return;
    try {
      navigator.sendBeacon?.(`${apiBase()}/api/client-error`, new Blob([raw], {
        type: 'application/json',
      }));
    } catch {
      /* the next load will send it */
    }
  });
}
